import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Divider, Card, Grid, Typography, Button } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useNotifications } from '../../../../_lib/notifications/useNotifications';
import { PasswordField } from '../../../lib/PasswordField/PasswordField';
import { updateUserPassword } from '../../../../actions/ProfileActions';
import { CreatePasswordField } from '../../../lib/CreatePasswordField/CreatePasswordField';
import withNewThemeProvider from '../../../hocs/withNewThemeProvider';

const initialErrors = {
  genericError: '',
  oldPassword: undefined,
  confirmPassword: undefined,
  password: undefined,
};

const useStyles = makeStyles(() => ({
  spacing: {
    margin: '0 5rem',
    paddingTop: '.8rem',
  },
  divider: {
    marginTop: '2.5rem',
    marginBottom: '1.6666666667rem',
  },
}));

function ChangePassword() {
  const classes = useStyles();
  const notifications = useNotifications();
  const dispatch = useDispatch();

  const [oldPassword, setOldPassword] = useState('');
  const [password, setPassword] = useState('');
  const [confirmedPassword, setConfirmedPassword] = useState('');
  const [valid, setValid] = useState(false);
  const [errors, setErrors] = useState<any>(initialErrors);
  const [disabled, setDisabled] = useState(false);

  const boundUpdateUserPassword = useCallback(
    (payload: any, onSuccess: any, onError: any) =>
      dispatch(updateUserPassword(payload, onSuccess, onError) as any),
    [dispatch]
  );

  const resetForm = () => {
    setOldPassword('');
    setPassword('');
    setConfirmedPassword('');
    setValid(false);
    setErrors(initialErrors);
    setDisabled(false);
  };

  // Mirrors the original checkPasswords, run after each field change.
  useEffect(() => {
    setDisabled(false);

    if (!confirmedPassword || !password || !oldPassword) {
      if (password !== confirmedPassword) {
        setErrors({ confirmPassword: 'Passwords do not match', valid: false });
      }
      return;
    }

    if (password === confirmedPassword) {
      setValid(true);
      setErrors(initialErrors);
    } else {
      setErrors({ confirmPassword: 'Passwords do not match' });
      setValid(false);
      setDisabled(true);
    }
  }, [oldPassword, password, confirmedPassword]);

  const success = (resp: any) => {
    if (resp.status === 200) {
      notifications.success('Password Successfully Updated!');
      resetForm();
    }
  };

  const error = (resp: any) => {
    if (resp.status === 400) {
      if (resp?.data === '') {
        setErrors({ oldPassword: 'Incorrect Password' });
        setDisabled(false);
      } else {
        setErrors({
          password: resp.data.errors,
          confirmPassword: 'Password fails security check',
        });
        setDisabled(false);
      }
    }
  };

  const handleChange = (name: string) => (event: any) => {
    const { value } = event.target;
    if (name === 'oldPassword') {
      setOldPassword(value);
    } else if (name === 'confirmedPassword') {
      setConfirmedPassword(value);
    }
  };

  const handlePasswordOnChange =
    (passwordInputName: string) => (value: any) => {
      if (passwordInputName === 'password') {
        setPassword(value);
      }
    };

  const handlePasswordErrors = (passwordErrors: any) => {
    if (!passwordErrors) {
      setValid(true);
      setErrors({ password: undefined });
      return;
    }
    setErrors({ password: passwordErrors });
    setValid(false);
  };

  const handleSubmit = (event: any) => {
    event.preventDefault();

    if (oldPassword === password) {
      setErrors({
        genericError:
          'New password cannot be the same as your previous password',
      });
      return;
    }

    setDisabled(true);

    boundUpdateUserPassword(
      { oldPassword, password, confirmedPassword },
      success,
      error
    );
  };

  return (
    <Card>
      <Box pl={3} pb={3} pr={3}>
        <Grid item md={12}>
          <Box py={2}>
            <Typography>CHANGE PASSWORD</Typography>
            <Divider />
          </Box>

          <form onSubmit={handleSubmit}>
            <Grid container direction="row" className={classes.spacing}>
              <Grid item md={5} direction="column">
                <PasswordField
                  id="oldPassword"
                  name="oldPassword"
                  label="Current Password"
                  required
                  error={errors.oldPassword}
                  helperText={errors.oldPassword}
                  value={oldPassword}
                  onChange={handleChange('oldPassword')}
                  fullWidth
                />
              </Grid>

              <Grid item md={1} />

              <Grid item md={5} container direction="column">
                {/* @ts-expect-error CreatePasswordField's typed props differ from the legacy id/value/onChange shape used here */}
                <CreatePasswordField
                  id="password"
                  label="Password"
                  required
                  value={password}
                  onChange={(e: any) =>
                    handlePasswordOnChange('password')(e.target.value)
                  }
                  onValidationErrors={handlePasswordErrors}
                />
                <span className="error-text">{errors.genericError}</span>

                <div style={{ paddingTop: '.8rem' }}>
                  <PasswordField
                    id="confirmedPassword"
                    name="confirmedPassword"
                    label="Confirm Password"
                    required
                    value={confirmedPassword}
                    onChange={handleChange('confirmedPassword')}
                    error={errors.confirmPassword}
                    helperText={errors.confirmPassword}
                    fullWidth
                  />
                </div>
              </Grid>
            </Grid>

            <Divider className={classes.divider} />
            <Box style={{ textAlign: 'center' }}>
              <Button
                size="large"
                variant="contained"
                color="primary"
                disabled={!valid || disabled}
                type="submit"
              >
                Change Password
              </Button>
            </Box>
          </form>
        </Grid>
      </Box>
    </Card>
  );
}

export default withNewThemeProvider(ChangePassword);
